/**
 * Event Processor
 *
 * Unified event processing system that orchestrates:
 * - Building standardized event payloads
 * - Applying filters at multiple stages
 * - Adapting data for each active pixel
 * - Dispatching to browser pixels and S2S endpoints
 *
 * @since 1.51.0
 */

(function (wpm, $, undefined) {

	/**
	 * Global event context that's added to all events
	 *
	 * @returns {object} Context object with user, session, and page data
	 */
	wpm.getEventContext = function () {
		return {
			timestamp : Math.floor(Date.now() / 1000),
			url       : window.location.href,
			referrer  : wpm.retrieveData("referrer") ? wpm.makeFullUrl(wpm.retrieveData("referrer")) : document.referrer,
			user_agent: navigator.userAgent,
			page_type : wpmDataLayer?.shop?.page_type,
			user_id   : wpmDataLayer?.user?.id?.raw || null,
			// Additional context can be added here
		};
	};

	/**
	 * Process an event through the unified pipeline
	 *
	 * This is the main entry point for processing events with the new filter system.
	 *
	 * @param {string} eventName - Standardized event name (e.g., 'add_to_cart', 'view_item')
	 * @param {object} eventData - Core event data (product, cart, order, etc.)
	 * @returns {Promise<object>} The processed payload
	 */
	wpm.processEvent = async function (eventName, eventData) {

		pmw.console.log(`Processing event: ${eventName}`, eventData);

		try {
			// 1. Create base payload with standardized structure
			let payload = {
				event     : eventName,
				event_data: eventData,
				pixels    : {},
				context   : wpm.getEventContext(),
			};

			// 2. Apply pre-processing filter
			// Use case: Add global tracking params, modify core data before pixel adaptations
			payload = pmw.hooks.applyFilters("pmw_event_payload_pre", payload, eventName);

			// 3. Transform for each active pixel
			const activePixels = wpm.pixelRegistry.getActivePixels();

			for (const pixelName of activePixels) {
				const adapter = wpm.pixelRegistry.getAdapter(pixelName);

				if (!adapter || typeof adapter.adapt !== "function") {
					pmw.console.log(`No adapter found for pixel: ${pixelName}`);
					continue;
				}

				try {
					// Adapt event data to pixel-specific format
					let pixelData = adapter.adapt(payload.event_data, eventName, payload.context);

					if (!pixelData) {
						// Some pixels (like Hotjar, VWO) don't track events, only load tracking scripts
						// So null is expected and not an error - skip silently
						continue;
					}

					// Apply pixel-specific filter
					// Use case: Modify Facebook-specific data, add custom parameters per pixel
					pixelData = pmw.hooks.applyFilters(
						`pmw_pixel_data_${pixelName}`,
						pixelData,
						eventName,
						payload.event_data,
						payload.context,
					);

					payload.pixels[pixelName] = pixelData;
				} catch (e) {
					pmw.console.error(`Error adapting data for ${pixelName}:`, e);
				}
			}

			// 4. Apply event-specific filter
			// Use case: Modify all pixels for a specific event type (e.g., all add_to_cart events)
			payload = pmw.hooks.applyFilters(
				`pmw_event_payload_${eventName}`,
				payload,
				payload.event_data,
			);

			// 5. Apply post-processing filter
			// Use case: Final validation, logging, debugging, cross-pixel modifications
			payload = pmw.hooks.applyFilters("pmw_event_payload_post", payload, eventName);

			// 6. Dispatch to pixels
			await wpm.dispatchToPixels(payload);

			return payload;

		} catch (e) {
			pmw.console.error(`Error processing event ${eventName}:`, e);
			throw e;
		}
	};

	/**
	 * Dispatch the processed payload to pixels
	 *
	 * This handles both S2S-capable pixels (which need server requests)
	 * and browser-only pixels (which are handled by their event listeners)
	 *
	 * @param {object} payload - The fully processed event payload
	 * @returns {Promise<void>}
	 */
	wpm.dispatchToPixels = async function (payload) {

		const eventName = payload.event;

		// Dispatch to browser-only pixels
		wpm.dispatchToBrowserPixels(payload);

		// Dispatch to S2S-capable pixels
		// These pixels handle both browser (fbq, ttq, etc.) and server-side events
		// They listen to pmw:s2s:* events to get the adapted data with event_id for deduplication
		wpm.dispatchToS2SPixels(payload);

		// Purchase events are handled entirely server-side and should never be sent from browser to server
		// to prevent duplicates and race conditions. However, browser-side pixels still need to fire.
		if (eventName === "purchase") {
			pmw.console.log("Purchase event - skipping browser-to-server transmission (handled server-side only)");
			return; // Return early, but browser pixels have already been dispatched above
		}

		// Only send to server if S2S is globally enabled
		if (wpmDataLayer?.general?.server_2_server?.active) {
			// Extract S2S data for server transmission
			const s2sData = wpm.extractS2SData(payload);

			// Send to server if there's S2S data to send
			if (Object.keys(s2sData).length > 0) {
				const s2sPixels = wpm.pixelRegistry.getActiveS2SPixels();
				pmw.console.log(`S2S enabled pixels for ${eventName}:`, s2sPixels);
				await wpm.sendEventPayloadToServer(s2sData);
			}
		}
	};
	/**ggers a browser-specific event that browser-only pixels can listen to.
	 * The payload includes adapted data for each browser-only pixel.
	 *
	 * @param {object} payload - The fully processed event payload
	 */
	wpm.dispatchToBrowserPixels = function (payload) {

		const browserPixels = wpm.pixelRegistry.getActiveBrowserOnlyPixels();

		if (browserPixels.length === 0) {
			return;
		}

		pmw.console.log(`Dispatching to browser-only pixels for ${payload.event}:`, browserPixels);

		// Trigger browser-specific event that browser-only pixels can listen to
		// This allows them to access the filtered, adapted data
		// Convert underscores to hyphens for event name (public API uses hyphens)
		const browserEventName = payload.event.replace(/_/g, "-");
		jQuery(document).trigger(`pmw:browser:${browserEventName}`, payload);
	};

	/**
	 * Dispatch to S2S-capable pixels
	 *
	 * Triggers events for S2S-capable pixels (Facebook, TikTok, Pinterest, Snapchat).
	 * These pixels handle both browser-side (fbq, ttq, etc.) and server-side events.
	 * They always receive the event to fire their browser pixels, regardless of S2S status.
	 *
	 * @param {object} payload - The fully processed event payload
	 */
	wpm.dispatchToS2SPixels = function (payload) {

		const s2sPixels = wpm.pixelRegistry.getActiveS2SPixels();

		if (s2sPixels.length === 0) {
			return;
		}

		// Trigger S2S event that S2S-capable pixels listen to
		// This provides adapted data with event_id for deduplication between browser and server
		// Convert underscores to hyphens for event name (public API uses hyphens)
		const s2sEventName = payload.event.replace(/_/g, "-");
		jQuery(document).trigger(`pmw:s2s:${s2sEventName}`, payload);
	};
	/**
	 * Extract server-to-server data from the payload
	 *
	 * Only S2S-capable pixels should be included in the server request.
	 * This creates a single payload with data for all S2S pixels.
	 *
	 * @param {object} payload - The full event payload
	 * @returns {object} S2S-only data for server transmission
	 */
	wpm.extractS2SData = function (payload) {

		const s2sData = {
			event: payload.event,
		};

		const s2sPixels = wpm.pixelRegistry.getActiveS2SPixels();

		for (const pixelName of s2sPixels) {
			if (payload.pixels[pixelName]) {
				s2sData[pixelName] = payload.pixels[pixelName];
			}
		}

		// Add product data if present (for server-side processing)
		if (payload.event_data?.product) {
			s2sData.product = payload.event_data.product;
		}

		return s2sData;
	};

	/**
	 * Initialize adapters namespace
	 */
	if (!wpm.adapters) {
		wpm.adapters = {};
	}

	/**
	 * Helper function to register an adapter with optional capabilities
	 *
	 * @param {string} pixelName - The pixel name
	 * @param {object} adapter - The adapter object with an adapt() method
	 * @param {object} [options] - Optional capabilities configuration
	 * @param {boolean} [options.supportsS2S=false] - Whether pixel supports server-to-server tracking
	 * @param {boolean} [options.supportsEventDeduplication=false] - Whether pixel supports event deduplication
	 * @param {function} [options.isActive] - Function that returns true if pixel is active/loaded
	 */
	wpm.registerAdapter = function (pixelName, adapter, options = {}) {
		if (!adapter || typeof adapter.adapt !== "function") {
			pmw.console.error(`Invalid adapter for ${pixelName}: must have an adapt() method`);
			return;
		}

		// Store the adapter
		wpm.adapters[pixelName] = adapter;

		// Store pixel capabilities
		if (!wpm.pixelCapabilities) {
			wpm.pixelCapabilities = {};
		}

		wpm.pixelCapabilities[pixelName] = {
			supportsS2S               : options.supportsS2S || false,
			supportsEventDeduplication: options.supportsEventDeduplication || false,
			isActive                  : options.isActive || null,
		};

		// pmw.console.log(`Adapter registered for: ${pixelName}`, wpm.pixelCapabilities[pixelName]);
	};

}(window.wpm = window.wpm || {}, jQuery));
